import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';
import ConfigContext from '../../contexts/ConfigContext';
import usePersistedState from '../../hooks/usePersistedState';
import Popover from '../Popover';

const CONTAINER_STYLE = { zIndex: 30 };
const ARROW_STYLE = {};
const CONTENT_STYLE = { borderRadius: '12px' };

const Anchor = styled.div`
  display: flex;
  align-items: center;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 280px;
  padding: 8px 16px;
`;

const Title = styled.div`
  font-size: 14rem;
  font-weight: 700;
  line-height: 20rem;
  color: ${({ theme }) => theme.colors.black};
`;

const Body = styled.div`
  font-size: 12rem;
  line-height: 17rem;
  color: ${({ theme }) => theme.colors.black};
  white-space: pre-line;
`;

const ConfirmButton = styled.button.attrs(() => ({ type: 'button' }))`
  align-self: flex-end;
  padding: 2px 8px;
  border: none;
  border-radius: 6px;
  font-size: 14rem;
  line-height: 20rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) =>
    `linear-gradient(91.68deg, ${theme.colors.primary} 3.74%, ${theme.colors.purple} 59.14%)`};
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

// A one-time hint anchored to the element it explains: opens by default, and once the user confirms
// it is never rendered again on this browser. Pass `visible={false}` to opt out entirely (e.g. behind
// a feature flag) — the children are then rendered untouched, with no extra wrapper and no storage writes.
const OnboardingHint = ({
  storageKey,
  title,
  body,
  confirmLabel,
  positions = ['left'],
  align = 'center',
  visible = true,
  className,
  children
}) => {
  const { t } = useContext(ConfigContext);
  const [isDismissed, setIsDismissed] = usePersistedState(visible ? storageKey : null, false, {
    persistBooleans: true
  });

  const handleDismiss = useCallback(() => setIsDismissed(true), [setIsDismissed]);

  if (!visible) {
    return children;
  }

  return (
    <Popover
      isPopoverOpen={!isDismissed}
      positions={positions}
      align={align}
      containerStyle={CONTAINER_STYLE}
      arrowStyle={ARROW_STYLE}
      popoverContentStyle={CONTENT_STYLE}
      triggerContent={<Anchor className={className}>{children}</Anchor>}
      popoverContent={
        <Card data-testid="onboarding-hint">
          <Title>{title}</Title>

          <Body>{body}</Body>

          <ConfirmButton onClick={handleDismiss} data-testid="onboarding-hint-confirm">
            {confirmLabel || t('gotIt')}
          </ConfirmButton>
        </Card>
      }
    />
  );
};

export default OnboardingHint;
